import type { DocumentTypeKey } from "../primitives.js"
import { documentTypeRegistryDefinitions } from "../registries.js"
import {
  domainEventTypes,
  exportTemplates,
  isAccountingExportTemplate,
  reviewFlagTypes,
  type DomainEventType,
  type ExportTemplate,
} from "../taxonomy.js"
import {
  workflowArrivalSources,
  workflowReconciliationStatuses,
  type WorkflowActionType,
  type WorkflowComparison,
  type WorkflowConditionField,
  type WorkflowPrerequisiteKey,
  type WorkflowValueKind,
} from "./vocabulary.js"

/* Three catalogues: what can start a rule, what it can check, what it can do.
   The editor renders straight from these, the API validates against them, and
   the engine executes what they describe, so a word cannot be offered in one
   place and be unknown in another. Every label is sentence case, written the
   way a bookkeeper would say it out loud. */

// --- what can start a workflow ----------------------------------------------

export type WorkflowTriggerCatalogEntry = {
  readonly value: DomainEventType
  /** The label in the trigger picker. */
  readonly label: string
  /** One plain sentence about when it fires. */
  readonly description: string
  /**
   * Whether one event turns into one run per document in it. Only a batch does:
   * every other trigger is already about a single document. Fanning out is what
   * makes the checks and the actions mean the same thing on every trigger.
   */
  readonly fansOutPerDocument: boolean
}

export const workflowTriggerCatalog: readonly WorkflowTriggerCatalogEntry[] = [
  {
    value: "document_approved",
    label: "A document is approved",
    description:
      "Fires the moment a reviewer approves the extracted data. The most useful trigger there is, because an approval is a person saying the numbers are right.",
    fansOutPerDocument: false,
  },
  {
    value: "document_processed",
    label: "A document finishes extracting",
    description:
      "Fires when extraction finishes, before anyone has looked at it.",
    fansOutPerDocument: false,
  },
  {
    value: "flags_created",
    label: "Something gets flagged",
    description: "Fires when extraction raises review flags on a document.",
    fansOutPerDocument: false,
  },
  {
    value: "batch_completed",
    label: "A batch finishes",
    description:
      "Fires once for each document in an upload or forwarded batch, after every document in it is done.",
    fansOutPerDocument: true,
  },
  {
    value: "export_generated",
    label: "An export is ready",
    description: "Fires when an export file finishes building.",
    fansOutPerDocument: false,
  },
  {
    value: "posting_completed",
    label: "A document finishes sending",
    description: "Fires when a send to QuickBooks finishes or fails.",
    fansOutPerDocument: false,
  },
  {
    value: "artifact_expired",
    label: "A file expires",
    description: "Fires when a stored original passes its retention window.",
    fansOutPerDocument: false,
  },
  {
    value: "purge_completed",
    label: "A deletion completes",
    description: "Fires when a deletion request finishes purging its files.",
    fansOutPerDocument: false,
  },
]

/**
 * The triggers a customer can choose, written out so Zod can build an enum of
 * six literals from them. This tuple is the source: the catalogue documents all
 * eight domain events so a new one cannot ship without a decision about it, and
 * which of them are offered is decided here. Deriving the tuple from a flag on
 * the entries instead would widen it to all eight and lose the literal types.
 */
export const selectableWorkflowTriggerValues = [
  "document_approved",
  "document_processed",
  "flags_created",
  "batch_completed",
  "export_generated",
  "posting_completed",
] as const satisfies readonly DomainEventType[]
const selectableWorkflowTriggerSet = new Set<string>(
  selectableWorkflowTriggerValues
)

/** The catalogue entries for the triggers a customer can choose, in order. */
export const selectableWorkflowTriggers = workflowTriggerCatalog.filter(
  (entry) => selectableWorkflowTriggerSet.has(entry.value)
)

export function workflowTriggerEntry(
  value: DomainEventType
): WorkflowTriggerCatalogEntry | undefined {
  return workflowTriggerCatalog.find((entry) => entry.value === value)
}

export function isSelectableWorkflowTrigger(
  value: string
): value is DomainEventType {
  return selectableWorkflowTriggerSet.has(value)
}

// --- what it can check -------------------------------------------------------

export type WorkflowComparisonCatalogEntry = {
  readonly value: WorkflowComparison
  /** The middle control's label, which reads as the verb of the sentence. */
  readonly label: string
  readonly valueKind: WorkflowValueKind
  /** Printed after the value control, so "is in the last 30 days" reads whole. */
  readonly valueSuffix?: string
}

export type WorkflowFieldCatalogEntry = {
  readonly value: WorkflowConditionField
  readonly label: string
  /** What the check reads, said plainly, for the editor's helper line. */
  readonly readsFrom: string
  /**
   * The document types this field exists on. `null` means every type, which is
   * also what a rule with no document type on its trigger is limited to.
   */
  readonly documentTypeKeys: readonly DocumentTypeKey[] | null
  readonly comparisons: readonly WorkflowComparisonCatalogEntry[]
}

/**
 * The types the amount, currency, counterparty and date checks are offered on.
 *
 * This is a deliberate subset, not the set of types that happen to carry those
 * values. Five of the six do: the extractors map a counterparty, a total and a
 * currency into the projection for purchase orders and pay stubs as well, and
 * the evaluator would compare them correctly. Two reasons it is still these
 * three:
 *
 * - the requirements enumerate exactly these three for every one of those
 *   fields, and widening the surface is a product decision rather than a
 *   correctness fix. It fails closed: a rule is refused, never fired wrongly;
 * - a pay stub's projected total is its net pay, so a field labelled "total
 *   amount" would quietly mean something narrower than a reader expects. That
 *   needs its own label before it needs its own rule.
 *
 * A purchase order has no such ambiguity and is the strongest candidate if this
 * is revisited. The exclusion is pinned by a test so it stays a decision rather
 * than becoming drift.
 */
const financialDocumentTypeKeys = [
  "bank_statement",
  "invoice",
  "receipt",
] as const satisfies readonly DocumentTypeKey[]

export const workflowFieldCatalog: readonly WorkflowFieldCatalogEntry[] = [
  {
    value: "unresolved_flags",
    label: "Unresolved flags",
    readsFrom: "Open review flags on the document",
    documentTypeKeys: null,
    comparisons: [
      { value: "is_none", label: "is none", valueKind: "none" },
      { value: "is_any", label: "is any", valueKind: "none" },
      { value: "count_over", label: "count is over", valueKind: "count" },
    ],
  },
  {
    value: "flag_kind",
    label: "Flag kind",
    readsFrom: "The kinds of open review flag on the document",
    documentTypeKeys: null,
    comparisons: [
      { value: "includes", label: "includes", valueKind: "flag_type" },
      {
        value: "does_not_include",
        label: "does not include",
        valueKind: "flag_type",
      },
    ],
  },
  {
    value: "total_amount",
    label: "Total amount",
    readsFrom: "The document total, or the transaction total on a statement",
    documentTypeKeys: financialDocumentTypeKeys,
    comparisons: [
      { value: "is_over", label: "is over", valueKind: "amount" },
      { value: "is_under", label: "is under", valueKind: "amount" },
      { value: "is_between", label: "is between", valueKind: "amount_range" },
    ],
  },
  {
    value: "currency",
    label: "Currency",
    readsFrom: "The currency on the document",
    documentTypeKeys: financialDocumentTypeKeys,
    comparisons: [
      { value: "is", label: "is", valueKind: "currency_code" },
      { value: "is_not", label: "is not", valueKind: "currency_code" },
    ],
  },
  {
    value: "counterparty",
    label: "Who it is from",
    readsFrom: "The merchant, vendor or institution, whichever the type has",
    documentTypeKeys: financialDocumentTypeKeys,
    comparisons: [
      { value: "is", label: "is", valueKind: "text" },
      { value: "contains", label: "contains", valueKind: "text" },
      {
        value: "does_not_contain",
        label: "does not contain",
        valueKind: "text",
      },
    ],
  },
  {
    value: "document_date",
    label: "Document date",
    readsFrom: "The transaction, invoice or period end date",
    documentTypeKeys: financialDocumentTypeKeys,
    comparisons: [
      {
        value: "in_last_days",
        label: "is in the last",
        valueKind: "days",
        valueSuffix: "days",
      },
      { value: "is_before", label: "is before", valueKind: "date" },
      { value: "is_after", label: "is after", valueKind: "date" },
    ],
  },
  {
    value: "reconciliation_status",
    label: "Reconciliation",
    readsFrom: "Whether the statement balances",
    documentTypeKeys: ["bank_statement"],
    comparisons: [
      { value: "is", label: "is", valueKind: "reconciliation_status" },
      { value: "is_not", label: "is not", valueKind: "reconciliation_status" },
    ],
  },
  {
    value: "arrival_source",
    label: "How it arrived",
    readsFrom: "Whether it was uploaded, forwarded by email, or sent by API",
    documentTypeKeys: null,
    comparisons: [
      { value: "is", label: "is", valueKind: "arrival_source" },
      { value: "is_not", label: "is not", valueKind: "arrival_source" },
    ],
  },
  {
    value: "page_count",
    label: "Page count",
    readsFrom: "The billed page count on the document",
    documentTypeKeys: null,
    comparisons: [
      { value: "is_over", label: "is over", valueKind: "count" },
      { value: "is_under", label: "is under", valueKind: "count" },
    ],
  },
  {
    value: "category",
    label: "Category",
    readsFrom: "The categories on the statement's transactions",
    // Bank statements only: categories exist on transaction rows, which no
    // other type carries. Widening this when another type gains enriched
    // rows is a product decision, pinned by the same per-type test as the
    // financial trio above.
    documentTypeKeys: ["bank_statement"],
    comparisons: [
      { value: "includes", label: "includes", valueKind: "category" },
      {
        value: "does_not_include",
        label: "does not include",
        valueKind: "category",
      },
      { value: "is", label: "is only", valueKind: "category" },
      { value: "is_not", label: "is not", valueKind: "category" },
    ],
  },
]

export function workflowFieldEntry(
  field: string
): WorkflowFieldCatalogEntry | undefined {
  return workflowFieldCatalog.find((entry) => entry.value === field)
}

export function workflowComparisonEntry(
  field: string,
  comparison: string
): WorkflowComparisonCatalogEntry | undefined {
  return workflowFieldEntry(field)?.comparisons.find(
    (entry) => entry.value === comparison
  )
}

/**
 * Whether a field can be checked on a rule scoped to this document type. A rule
 * with no document type is every type, so only the fields every type has are
 * offered: a check that could not be evaluated is worse than one we never
 * offered.
 */
export function fieldAppliesToDocumentType(
  entry: WorkflowFieldCatalogEntry,
  documentTypeKey: DocumentTypeKey | null
): boolean {
  if (entry.documentTypeKeys === null) {
    return true
  }

  if (documentTypeKey === null) {
    return false
  }

  return entry.documentTypeKeys.includes(documentTypeKey)
}

/** The fields the editor offers for a rule scoped to this document type. */
export function workflowFieldsForDocumentType(
  documentTypeKey: DocumentTypeKey | null
): readonly WorkflowFieldCatalogEntry[] {
  return workflowFieldCatalog.filter((entry) =>
    fieldAppliesToDocumentType(entry, documentTypeKey)
  )
}

/** The choices behind a value control, for the kinds that have a fixed list. */
export const workflowValueOptions = {
  flag_type: reviewFlagTypes,
  reconciliation_status: workflowReconciliationStatuses,
  arrival_source: workflowArrivalSources,
} as const

/** Sentence-case labels for the fixed lists, so the editor never shows an enum. */
export const workflowFlagTypeLabels = {
  missing_required_field: "Missing required field",
  low_confidence: "Low confidence",
  amount_mismatch: "Amount mismatch",
  date_out_of_range: "Date out of range",
  duplicate_record: "Duplicate record",
  unsupported_layout: "Unsupported layout",
  provider_warning: "Provider warning",
  policy_violation: "Policy violation",
} satisfies Record<(typeof reviewFlagTypes)[number], string>

export const workflowReconciliationStatusLabels = {
  balanced: "Balanced",
  out_of_balance: "Out of balance",
  not_available: "Not available",
} satisfies Record<(typeof workflowReconciliationStatuses)[number], string>

export const workflowArrivalSourceLabels = {
  upload: "Uploaded",
  email: "Forwarded by email",
  api: "Sent by API",
} satisfies Record<(typeof workflowArrivalSources)[number], string>

// --- what it can do ----------------------------------------------------------

export type WorkflowActionCatalogEntry = {
  readonly value: WorkflowActionType
  readonly label: string
  readonly description: string
  /** Everything that has to be true before this action can run. */
  readonly prerequisites: readonly WorkflowPrerequisiteKey[]
  /**
   * Whether running it sends something out of LedgerBox. Turning on a rule
   * with one of these asks for confirmation naming the destination; a rule that
   * only builds a file inside LedgerBox does not.
   */
  readonly leavesLedgerBox: boolean
  /**
   * The domain events this action can cause. A rule triggered by an event its
   * own action produces is a loop, and this is what the loop check reads, at
   * save time and again at run time.
   */
  readonly producesEventTypes: readonly DomainEventType[]
  /**
   * The document types the action can act on, or `null` for every type. Derived
   * from the type registry where there is a registry answer, so a type that
   * gains a posting capability gains the action with it.
   */
  readonly documentTypeKeys: readonly DocumentTypeKey[] | null
}

/**
 * The types QuickBooks sending understands, read from the registry rather than
 * listed again here. A type with no posting capability cannot be sent, and
 * offering the action on one would be a promise we cannot keep.
 */
export const postableDocumentTypeKeys = documentTypeRegistryDefinitions
  .filter((definition) => definition.postingCapabilities.length > 0)
  .map((definition) => definition.key)

export const workflowActionCatalog: readonly WorkflowActionCatalogEntry[] = [
  {
    value: "post_to_quickbooks",
    label: "Send to QuickBooks",
    description:
      "Creates the same journal entry the send button creates, against the accounts already chosen in settings.",
    prerequisites: [
      "pro_plan",
      "quickbooks_connected",
      "quickbooks_destination",
    ],
    leavesLedgerBox: true,
    producesEventTypes: ["posting_completed"],
    documentTypeKeys: postableDocumentTypeKeys,
  },
  {
    value: "email_copy",
    label: "Email a copy",
    description:
      "Sends the extracted figures to people who have confirmed their address, with the original file attached only if you ask for it.",
    prerequisites: ["confirmed_recipients"],
    leavesLedgerBox: true,
    producesEventTypes: [],
    documentTypeKeys: null,
  },
  {
    value: "send_to_webhook",
    label: "Send to a webhook endpoint",
    description:
      "Fires at one endpoint you have already set up, signed and logged exactly as a subscription is.",
    prerequisites: ["pro_plan", "webhook_endpoint"],
    leavesLedgerBox: true,
    producesEventTypes: [],
    documentTypeKeys: null,
  },
  {
    value: "build_export",
    label: "Build an export",
    description:
      "Produces a file on one of the templates this document type supports.",
    prerequisites: [],
    leavesLedgerBox: false,
    producesEventTypes: ["export_generated"],
    documentTypeKeys: null,
  },
]

export function workflowActionEntry(
  actionType: string
): WorkflowActionCatalogEntry | undefined {
  return workflowActionCatalog.find((entry) => entry.value === actionType)
}

export function actionAppliesToDocumentType(
  entry: WorkflowActionCatalogEntry,
  documentTypeKey: DocumentTypeKey | null
): boolean {
  if (entry.documentTypeKeys === null) {
    return true
  }

  // A rule with no document type covers every type, including ones this action
  // cannot act on. It is still offered, because refusing it would stop a
  // perfectly good "post every receipt and invoice" rule, and the run records a
  // clear reason on a document the action cannot handle.
  if (documentTypeKey === null) {
    return true
  }

  return entry.documentTypeKeys.includes(documentTypeKey)
}

/**
 * The export templates a document type supports, straight from the registry.
 *
 * The registry types its templates as plain strings, so they are narrowed here
 * against the template vocabulary the save schema validates against. A registry
 * entry the vocabulary does not know is dropped rather than offered: offering a
 * template the save would reject is a dead end a customer cannot get out of.
 */
const exportTemplatesByDocumentType = new Map<
  DocumentTypeKey,
  readonly ExportTemplate[]
>(
  documentTypeRegistryDefinitions.map((definition) => {
    const known: ExportTemplate[] = []

    for (const template of definition.exportTemplates) {
      if (isExportTemplate(template)) {
        known.push(template)
      }
    }

    return [definition.key, known]
  })
)

function isExportTemplate(template: string): template is ExportTemplate {
  return new Set<string>(exportTemplates).has(template)
}

export function exportTemplatesForDocumentType(
  documentTypeKey: DocumentTypeKey
): readonly ExportTemplate[] {
  return exportTemplatesByDocumentType.get(documentTypeKey) ?? []
}

/**
 * Every template offered to a rule with no document type: the union across the
 * types, because the run picks a template the document actually supports and
 * fails clearly when it does not.
 */
const everyWorkflowExportTemplate = [
  ...new Set([...exportTemplatesByDocumentType.values()].flat()),
]

export function allWorkflowExportTemplates(): readonly ExportTemplate[] {
  return everyWorkflowExportTemplate
}

/**
 * What has to be true before this action can run, given how it is configured.
 *
 * The catalogue entry cannot know a rule's settings, and one of them changes
 * the answer: the QuickBooks export templates need a paid, active plan exactly
 * as they do on a manual export, while LedgerBox CSV and JSON stay open to
 * everyone. The catalogue would otherwise declare that building an export needs
 * nothing, and a trial would build a rule that always fails.
 */
export function workflowActionPrerequisites(action: {
  readonly actionType: string
  readonly template?: string
}): readonly WorkflowPrerequisiteKey[] {
  const entry = workflowActionEntry(action.actionType)

  if (!entry) {
    return []
  }

  if (
    entry.value === "build_export" &&
    isAccountingExportTemplate(action.template)
  ) {
    return [...entry.prerequisites, "paid_plan"]
  }

  return entry.prerequisites
}

// --- an assertion the three catalogues have to keep --------------------------

/**
 * Every domain event is either offered as a trigger or explicitly not, so a new
 * event type cannot ship without somebody deciding which. Exported rather than
 * asserted inline so the unit test can name the gap.
 */
export function undocumentedTriggerEventTypes(): readonly string[] {
  const documented = new Set(workflowTriggerCatalog.map((entry) => entry.value))

  return domainEventTypes.filter((value) => !documented.has(value))
}
